import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { AppUseSelector, AppUseDispatch } from '../../redux/hooks'
import {
  getStory,
  setStep,
  saveLastStep,
  setPreventRedirect,
  setShowRecordingSuccess,
  wordDoesNotKnow,
  wordDoKnow,
  getWordDetail,
  preloadStoryDictionary,
} from '../../redux/actions/storyAction'
import { useWordAudio } from '../../hooks/useWordAudio'
import { useAudioPreloader } from '../../hooks/useAudioPreloader'
import { useKaraokeHighlight } from '../../hooks/useKaraokeHighlight'
import { buildKaraokeTimeline } from '../../utils/karaoke'
import { attachSentences, splitIntoParagraphs } from '../../utils/storySentences'
import { attachWordSenses } from '../../utils/wordSenses'
import posthog from 'posthog-js'
import { useTranslation } from 'react-i18next'
import { sentenceTranslationText, storyHasLocalizedContent } from '../../i18n/localizedContent'
import { languageStoryId, sentenceRefId } from 'containers/word-detail/reportTranslation'
import { getStoryAudio, storyAudioActiveWithin, KARAOKE_IDLE_MS } from '../../utils/storyAudio'
import { MemberStepInStory, storyStep } from '../../utils/constant'
import NavigationFooter from '../../containers/navigation-footer/NavigationFooter'
import { INTRODUCTION, QUIZ, HOME } from 'routes/routeConstant'
import StoryContent from 'containers/RenderStroyComponent/StoryContent'
import { AddWordToHackChinese, handleAddWordToSkritter } from '../../redux/actions/userAction'
import { getQuiz, getGeneralResult } from '../../redux/actions/quizAction'
import WithRedirect from 'containers/WithRedirect'
import { REGEX } from 'utils/regex'
import WordDetail from 'containers/word-detail'
import { converterWord, getWordLine, getWordTypeFromStory, wordSimplified, getSentenceWords } from 'utils/helperFunction'
import { hapticSelection } from 'utils/haptics'
import WordExamplesModal from 'containers/modal/word-examples-modal'
import ReaderSettings from 'containers/reader-settings/ReaderSettings'
import { CHECK_WORD_IS_CLICKED, RESET_QUIZ } from '../../redux/types'
import { readPreference, writePreference } from 'utils/readerPreferences'
import { useNavigate, useParams } from 'react-router-dom'
import OneLineContent from 'containers/RenderStroyComponent/OneLineContent'
import TapResult from './TapResult'

const capture = (event: string, properties: Record<string, unknown>) => {
  try {
    posthog.capture(event, properties)
  } catch {
    /* analytics must never break the reader */
  }
}

// const eyeIcon = <i className='fa fa-eye' />
// const eyeSlashIcon = <i className='fa fa-eye-slash' />

const Story: React.FC<any> = ({
  lastStep,
  lastStepEnum,
  isGetData,
  showRecordingSuccess,
  storyLevel,
  story,
  user_auth_token,
  skritter_token,
  credentials,
  quizDetail,
}) => {
  const { t } = useTranslation('reader')
  const { memberId } = credentials
  const { id: storyId = '' } = useParams()
  const dispatch = AppUseDispatch()
  const navigate = useNavigate()
  const { chineseWordFontSize } = AppUseSelector(({ dashboard }) => dashboard)
  const characterPreference = credentials?.customFields?.character
  const { wordDetails, isWordClick, storyDictionary } = AppUseSelector(({ story }) => story)
  const { stopCurrentAudio, isPlaying: isAudioPlaying } = useWordAudio()

  // Preload audio files for words in the current story
  useAudioPreloader(storyDictionary)
  const extraDivRef = useRef<HTMLDivElement>(null)
  const contentWordRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState('-1')
  const [storyWord, setStoryWord] = useState([...(story?.storyTitleContent || []), ...(story?.storyContent || [])])
  const [wordDetailVisible, setWordDetailVisible] = useState(false)
  const [openExampleModal, setOpenExampleModal] = useState(false)
  const [isImportanceOpen, setImportanceOpen] = useState(false)
  const [isMasteryOpen, setMasteryOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const audio = useMemo(() => story?.audio, [story])
  // Follow-along highlighting of the sentence/word being read in the recording.
  const karaokeTimeline = useMemo(() => buildKaraokeTimeline(story), [story])
  useKaraokeHighlight(audio, karaokeTimeline)
  // English under each Chinese sentence (reader settings) and in the word
  // panel. Null when the story has no sentence
  // translations yet or its text changed since: paragraph English is used.
  const sentences = useMemo(
    () => attachSentences(splitIntoParagraphs(story?.storyContent || []), story?.sentenceTranslations),
    [story],
  )
  // The sense (and reading) each word occurrence has in its sentence, when the API sent them.
  const occurrenceSenses = useMemo(
    () => attachWordSenses(splitIntoParagraphs(story?.storyContent || []), story?.wordSenses),
    [story],
  )
  const [showEnglish, setShowEnglish] = useState(() => readPreference('reader.showTranslations', false))
  // The translation is in the learner's language (sentenceTranslations[i].tr, localizedContent)
  // when the API sent it, English otherwise.
  const translationLocalized = useMemo(() => storyHasLocalizedContent(story), [story])
  const activeSentenceIndex = sentences?.sentenceByWordId.get(activeIndex)
  const activeSentence =
    wordDetailVisible && sentences && activeSentenceIndex !== undefined
      ? {
          index: activeSentenceIndex,
          ...sentences.rows[activeSentenceIndex],
          translation: sentenceTranslationText(story?.sentenceTranslations?.[activeSentenceIndex], story?.contentLanguage),
          refId: sentenceRefId(languageStoryId(story), story?.sentenceTranslations, activeSentenceIndex),
        }
      : null

  // story_opened, once per story, with the language its translation is served in. Property names
  // match the iOS and Android event (story_id, level, is_reread, has_audio).
  const trackedStory = useRef<string | null>(null)
  useEffect(() => {
    if (!story?.id || story.id !== storyId || trackedStory.current === story.id) return
    trackedStory.current = story.id
    capture('story_opened', {
      story_id: story.id,
      level: story?.level_display ?? story?.level ?? null,
      is_reread: !!story?.is_read,
      has_audio: !!story?.audio,
      has_localized_content: storyHasLocalizedContent(story),
      content_language: story?.contentLanguage ?? null,
    })
  }, [story, storyId])

  const toggleEnglish = () => {
    const next = !showEnglish
    setShowEnglish(next)
    writePreference('reader.showTranslations', next)
    capture('translation_toggled', { enabled: next, surface: 'settings', story_id: story?.id, sentence_level: !!sentences })
  }
  const karaokeSpanById = useMemo(
    () => (karaokeTimeline ? new Map(karaokeTimeline.map((span) => [span.id, span])) : null),
    [karaokeTimeline],
  )
  // const popupRef = useRef<HTMLInputElement>(null)
  // const contentStoryRef = useRef<HTMLDivElement>(null)
  // const [transVisible, setTransVisible] = useState(false)

  // useEffect(() => {
  //   document.addEventListener('scroll', onTranslateClose)
  //   document.addEventListener('click', onDocumentClick)
  //   return () => {
  //     document.removeEventListener('scroll', onTranslateClose)
  //     document.removeEventListener('click', onDocumentClick)
  //   }
  // }, [contentStoryRef, story])

  const handleClickOutside = (event) => {
    const isInsideSpecificButtons = event.target.closest('[data-specific-buttons]')
    if (
      !isInsideSpecificButtons &&
      !contentWordRef.current?.contains(event.target) &&
      !extraDivRef.current?.contains(event.target)
    ) {
      if (wordDetailVisible && wordDetails?.word && !isWordClick) {
        const mastery = getWordTypeFromStory(wordDetails?.word, story, characterPreference)
        wordDoesNotKnow({
          word: wordSimplified(wordDetails?.word),
          storyId,
          mastery: mastery === 'word' ? undefined : mastery,
          page: 'story',
        })
        dispatch({ type: CHECK_WORD_IS_CLICKED, payload: false })
      }
      stopCurrentAudio()
      setWordDetailVisible(false)
      setImportanceOpen(false)
      setMasteryOpen(false)
      setActiveIndex('-1')
    } else if (isInsideSpecificButtons) {
      const mastery = getWordTypeFromStory(wordDetails?.word, story, characterPreference)
      if (wordDetailVisible && wordDetails?.word && !isWordClick) {
        wordDoesNotKnow({
          word: wordSimplified(wordDetails?.word),
          storyId,
          mastery: mastery === 'word' ? undefined : mastery,
          page: 'story',
        })
        dispatch({ type: CHECK_WORD_IS_CLICKED, payload: false })
      }
    }
  }

  useEffect(() => {
    if (openExampleModal) {
      return
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [extraDivRef, contentWordRef, openExampleModal, wordDetailVisible, isWordClick, wordDetails])

  // const onTranslateClose = () => {
  //   setTransVisible(false)
  // }
  // const onDocumentClick = (e) => {
  //   if (!popupRef?.current?.contains(e.target)) {
  //     setTransVisible(false)
  //   }
  // }

  useEffect(() => {
    // A link straight into a story (the answer to a story email's quiz) can arrive while the
    // browser still holds the last story read. Load the story the URL names whenever the stored
    // one is a different story, and drop its quiz with it.
    const isOtherStory = !!storyId && !!story?.id && story.id !== storyId
    if (isOtherStory) {
      dispatch({ type: RESET_QUIZ })
      getStory(storyId, storyLevel)
      getQuiz(storyId, storyLevel).then((result: any) => {
        if (result?.memberAnswer) {
          getGeneralResult(storyId)
        }
      })
    } else if (JSON.stringify(story) === '{}') {
      getStory(storyId, storyLevel)
      if (!quizDetail?.quiz) {
        getQuiz(storyId, storyLevel).then((result: any) => {
          if (result.memberAnswer) {
            getGeneralResult(storyId)
          }
        })
      }
    } else {
      // Preload dictionary for instant word lookup
      preloadStoryDictionary(storyId)
    }

  }, [isGetData, storyId, storyLevel])

  useEffect(() => {
    window.scrollTo(0, 0)
    setStep(storyStep.Step1)

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (showRecordingSuccess) {
      setTimeout(() => setShowRecordingSuccess(false), 3000)
    }
  }, [showRecordingSuccess])

  useEffect(() => {
    if (
      storyId &&
      storyLevel &&
      lastStep !== MemberStepInStory.STORY_AND_LISTENING &&
      (lastStepEnum || lastStepEnum === 0) &&
      lastStepEnum < storyStep.Step1
    ) {
      saveLastStep(MemberStepInStory.STORY_AND_LISTENING, storyLevel, storyId)
    }
  }, [storyId, storyLevel])

  const backOnClick = () => {
    setPreventRedirect(true)
    navigate(INTRODUCTION)
  }

  const onNext = () => {
    const uniqueItems = {}
    storyWord.forEach((item) => {
      uniqueItems[item.word] = item
    })
    const wordList = Object.values(uniqueItems)
      ?.filter((wordInfo: any) => !REGEX.specialChars.test(wordInfo?.word))
      .map((wordInfo: any) => wordSimplified(wordInfo?.word))
    wordDoKnow({ wordList, storyId })
    navigate(`${QUIZ}/${storyId}`)
  }

  const handle = {
    onClickWord: (event, word, sentence, key) => {
      // While the learner is listening (playing, or paused for less than
      // KARAOKE_IDLE_MS), tapping a word snaps the recording to that word —
      // the definition lookup below still happens on the same tap. Once the
      // audio has been idle longer, a tap is a plain dictionary tap again
      // (mastery-colored word-active highlight, no karaoke visuals, no seek).
      if (karaokeSpanById && audio && storyAudioActiveWithin(KARAOKE_IDLE_MS)) {
        const span = karaokeSpanById.get(key)
        if (span) {
          getStoryAudio(audio).currentTime = span.startMs / 1000 + 0.01
        }
      }
      if (isMobile) {
        const targetElement = document.getElementById(key)
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth' })
        }
      }
      if (typeof word !== 'string' && !REGEX.specialChars.test(word?.word)) {
        // Subtle tick when a real word is tapped to reveal its definition.
        hapticSelection()
        const storyWords = storyWord
          .filter?.((wordInfo) => {
            if (
              wordInfo?.word.includes('<p>') ||
              wordInfo?.word.includes('</p>') ||
              wordInfo?.word.includes('</p><p>')
            ) {
            } else {
              return wordInfo
            }
          })
          ?.filter((wordInfo: any) => wordInfo?.word !== word?.word)
        setStoryWord(storyWords)
        handle.translateWord(event, sentence)
      }
      setActiveIndex(activeIndex === key ? `-1` : key)
      event.preventDefault()
      event.stopPropagation()
    },
    translateWord: (event: any, sentence: any) => {
      if (
        (event.type === 'mouseover' || event.type === 'click') &&
        event.target.innerText &&
        event.target.innerText.length > 0
      ) {
        const word = event.target.innerText
        const allDataOp = [...(story?.arContent || []), ...(story?.storyTitleContent || [])]
        if (/^[&.-”“，、；？@!#$%^&*()]+$/.test(word) || word.trim() === '。') {
          return
        }
        const wordTranslate = allDataOp?.filter((iword) => converterWord(iword?.word, characterPreference) === word)
        if (wordTranslate && wordTranslate.length > 0) {
          handle.getWordDetail(word, sentence, wordTranslate[0])
        }
      }
    },
    getWordDetail: async (word: string, sentence: any, data: any) => {
      const sentenceString = getWordLine(sentence)
      const basicDetail = {
        word,
        pinyin: data?.pinyin,
        english: data?.english,
        sentenceString,
        sentenceWords: getSentenceWords(sentence),
      }

      if (word && memberId) {
        setWordDetailVisible(true)

        const mastery = getWordTypeFromStory(word, story, characterPreference)
        await getWordDetail({
          detail: basicDetail,
          payload: { word: wordSimplified(word), memberId },
          wordType: mastery,
        }).then((res) => {
          handle.onWordClickIntegration({ word: res?.word, pinyin: res?.pinyin, english: res?.english })
        })
      }
    },

    onWordClickIntegration: (obj: any) => {
      if (user_auth_token) {
        AddWordToHackChinese({
          word: obj?.word,
          pinyin: obj?.pinyin,
          user_auth_token: user_auth_token,
        })
      }
      if (skritter_token) {
        handleAddWordToSkritter({
          word: obj?.word,
          translate: { pinyin: obj?.pinyin, english: obj?.english },
          title: story?.storyTitleContent,
          skritter_token: skritter_token,
        })
      }
    },
  }

  return (
    <>
      <div className='section-2'>
        <div className='container mb-0'>
          <div className='w-row  introduction-main-div mob_padding_lr'>
            <div className='w-col-12 w-col-medium-12 w-col-small-12 w-col-tiny-12'>
              {/* What they answered in the story email, celebrated over the story they landed in. */}
              <TapResult />
              <div className='pinyin_dropdown_head_div'>
                <ReaderSettings
                  showEnglish={showEnglish}
                  onToggleEnglish={toggleEnglish}
                  translationLocalized={translationLocalized}
                />
              </div>
            </div>
          </div>
        </div>
        <div className='story_sec_container'>
          <div className='w-col-12 w-col-medium-12 w-col-small-12 w-col-tiny-12  content-left mobile_remove_space_title mob_padding_lr'>
            <div className='section_story_container'>
              <span
                style={{
                  fontSize: chineseWordFontSize?.storyTitle,
                  lineHeight: 'initial',
                  letterSpacing: '1.5px',
                  display: 'flex',
                  flexWrap: 'wrap',
                }}
                className='introduction-title mob_linelight_remove'
              >
                <div className='word_wrapper'>
                  {story?.storyTitle &&
                    story?.storyTitleContent?.map((wordObj, index) => (
                      <OneLineContent
                        key={index}
                        WIndex={index}
                        iterator={'STORY-TITLE'}
                        isFontBold={true}
                        chineseWordFontSize={chineseWordFontSize}
                        wordObj={wordObj}
                        showPinYin={true}
                        contentDetail={story?.storyTitleContent || []}
                        story={story}
                        activeIndex={activeIndex}
                        handleClickWord={handle.onClickWord}
                      />
                    ))}
                </div>
              </span>
            </div>
          </div>
        </div>

        <div className='divider_line_story'></div>

        <div className='section-story story_sec_container'>
          <div className='mob_padding_lr'>
            {showRecordingSuccess && (
              <div className='recording-submitted-popup'>
                <h6>{t('story.recordingSubmitted')}</h6>
              </div>
            )}

            {story && story?.storyTitle && story?.storyContent ? (
              <div className='w-row section-story-content'>
                <StoryContent
                  story={story}
                  showTranslate={false}
                  showEnglish={showEnglish}
                  sentences={sentences}
                  wordSenses={occurrenceSenses}
                  showPinYin={true}
                  handleClickWord={handle.onClickWord}
                  activeIndex={activeIndex}
                  chineseWordFontSize={chineseWordFontSize}
                />
              </div>
            ) : (
              <div className='w-row'>
                <div className='w-col w-col-12' style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div className='item-title' style={{ marginBottom: '20px', fontSize: '32px', fontWeight: 'bold' }}>
                    {t('story.notFoundTitle')}
                  </div>
                  <div style={{ marginBottom: '30px', fontSize: '18px' }}>
                    <p>{t('story.notFoundBody')}</p>
                  </div>
                  <button className='button-2 w-button' onClick={() => navigate(HOME)} style={{ marginTop: '20px' }}>
                    {t('story.backHome')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <>
        <NavigationFooter
          audio={audio}
          handleClickWord={handle.onClickWord}
          backOnclick={backOnClick}
          nextPath={`${QUIZ}/${storyId}`}
          nextText={t('story.nextStep')}
          activeIndex={activeIndex}
          nextOnclick={onNext}
        />
      </>

      <WordDetail
        ref={contentWordRef}
        setOpenExampleModal={setOpenExampleModal}
        isImportanceOpen={isImportanceOpen}
        setImportanceOpen={setImportanceOpen}
        isMasteryOpen={isMasteryOpen}
        setMasteryOpen={setMasteryOpen}
        setWordDetailVisible={setWordDetailVisible}
        setActiveIndex={setActiveIndex}
        visible={wordDetailVisible}
        openExampleModal={openExampleModal}
        showMasterySection={true}
        isAudioPlaying={isAudioPlaying}
        sentence={activeSentence}
        contextSense={wordDetailVisible ? occurrenceSenses.get(activeIndex) ?? null : null}
        showEnglish={showEnglish}
        onSentenceRevealed={(index: number) =>
          capture('sentence_translation_revealed', { story_id: story?.id, sentence_index: index, surface: 'word_panel' })
        }
      />
      <WordExamplesModal
        ref={extraDivRef}
        openExampleModal={openExampleModal}
        setOpenExampleModal={setOpenExampleModal}
        setWordDetailVisible={setWordDetailVisible}
      />
      {/* <Translate ref={popupRef} visible={transVisible} data={transData} onClose={onTranslateClose} /> */}
    </>
  )
}

const mapStateToProps = (state: any) => ({
  user_auth_token: state.user.user_auth_token,
  skritter_token: state.user.skritter_token,
  credentials: state.user.credentials,
  membershipPlan: state.user.membership.name,
  membership: state.user.membership,
  story: state.story.currentStory,
  storyLevel: state.story.storyLevel,
  lastStep: state.story.lastStep,
  lastStepEnum: state.story.lastStepEnum,
  showRecordingSuccess: state.story.showRecordingSuccess,
  isGetData:
    // (state.user.credentials.level.length > 0 || state.user.credentials.customFields?.level.length > 0) &&
    JSON.stringify(state.story.currentStory) === '{}' || state.story.currentStory.id,
  quizDetail: state.quiz.quizDetail,
})

export default connect(mapStateToProps)(WithRedirect(Story))
